import type { QueryOptions } from "@tanstack/react-query"
import type { ExactPartial } from "@wagmi/core/internal"

import { isAddress } from "@eulerxyz/core-sdk"
import { Evaluate, getAddress, zeroAddress } from "viem"
import {
  type GetVaultErrorType,
  type GetVaultParameters,
  type GetVaultReturnType,
  getVault,
} from "../actions/getVault"
import type {
  ExtendedConfig,
  ScopeKeyParameter,
  SimulationParameter,
} from "../types"
import { filterQueryOptions } from "../utils"

export type GetVaultOptions = Evaluate<
  ExactPartial<GetVaultParameters> & ScopeKeyParameter & SimulationParameter
>

export function getVaultQueryKey(options: GetVaultOptions = {}) {
  const { address, evcAddress, vaultLensAddress, ...parameters } = options
  return [
    "eVault",
    filterQueryOptions({
      ...parameters,
      address:
        address && isAddress(address) ? getAddress(address) : zeroAddress,
      evcAddress: evcAddress && getAddress(evcAddress),
      vaultLensAddress: vaultLensAddress && getAddress(vaultLensAddress),
    }),
  ] as const
}

export function getVaultQueryFn(
  config: ExtendedConfig,
  options: GetVaultOptions = {},
) {
  const { ...parameters } = options

  return getVault(config, parameters)
}

export function getVaultQueryOptions<config extends ExtendedConfig>(
  config: config,
  options: GetVaultOptions,
) {
  return {
    gcTime: 0,
    queryFn: async ({ queryKey }) => {
      return getVaultQueryFn(config, queryKey[1])
    },
    queryKey: getVaultQueryKey(options),
  } satisfies QueryOptions<
    GetVaultQueryFnData,
    GetVaultErrorType,
    GetVaultData,
    GetVaultQueryKey
  >
}

export type GetVaultQueryFnData = GetVaultReturnType

export type GetVaultData = GetVaultQueryFnData

export type GetVaultQueryKey = ReturnType<typeof getVaultQueryKey>
